// Premature-handover classifier — for each target bot (any bot in the
// catalog with a `business_outcomes` block), looks for sessions that
// escalated to a human agent WITHOUT ever invoking one of the bot's
// declared business-outcome tools.
//
// WHY this matters:
//   A bot that transfers to a live agent at the end of a completed
//   booking is doing its job. A bot that transfers to a live agent
//   WITHOUT ever having tried its primary workflow — never called
//   ScheduleAppointment, never called SendMyChartMessage — is bailing
//   before it tried. These "premature" handovers are the KPI that
//   tells operations whether a bot is actually attempting the work it
//   was built for, versus just acting as an expensive routing layer.
//
// WHY it's deep:
//   `lastAction` only captures the LAST tool invoked. If a bot calls
//   ScheduleAppointment successfully and then calls Agent handover for
//   the closing wrap, `lastAction = Agent handover` — a session-row
//   scan can't tell "handover with no prior work" from "handover after
//   the work was done". The ground truth is in `metadata.tools[]` on
//   the message trail.
//
// PRE-FILTER:
//   To keep the fetch cheap, we only call fetchMessages on sessions
//   where it's possible for a handover to have occurred:
//     interactionOutcome === "ESCALATED" OR lastAction === "Agent handover"
//   For a 24h ortho window with ~1000 sessions, this cuts the fetch
//   set to typically 30-80 sessions.
//
// SIGNALS consumed in messages (shape verified against prod 2026-10-23):
//   metadata.tools[].tool_name   — the Agent Studio tool name
//   metadata.tools[].state       — only SUCCESS invocations count as
//                                   "the bot tried the workflow"; a
//                                   failed tool call is a legitimate
//                                   reason to escalate, so it doesn't
//                                   count against the bot.
//
// Business-outcome tool-set is pulled from botCatalog.json → bots[]
// .business_outcomes[].tool_names (same source Business Outcomes uses).

/**
 * Classify premature handovers across the window.
 *
 * @param {Array} sessions - contactAIAgentSession rows
 * @param {object} catalog - botCatalog.json
 * @param {object} opts - {
 *   agentIdByName: { [botName]: agentId },
 *   agentIdToName?: { [agentId]: botName },
 *   fetchMessages: async (agentId, sessionId) => { messages: [] },
 *   onProgress?: (done, total) => void,
 *   signal?: AbortSignal,
 * }
 * @returns {Promise<{
 *   perBot: { [botName]: {
 *     totalHandovers, premature, rate, avgDurationMs,
 *     prematureSessionIds: Set<string>,
 *   } },
 *   totalScanned: number,
 * }>}
 */
export async function classifyPrematureHandovers(sessions, catalog, opts) {
  const agentIdToName = opts.agentIdToName ?? {};
  const resolveBotName = (s) =>
    s.agentName || (s.agentId && agentIdToName[s.agentId]) || '';

  // Build { botName: Set<toolName> } from the catalog's business_outcomes.
  const outcomeToolsByBot = new Map();
  for (const [botName, bot] of Object.entries(catalog?.bots ?? {})) {
    const defs = bot?.business_outcomes ?? {};
    for (const def of Object.values(defs)) {
      if (!outcomeToolsByBot.has(botName)) outcomeToolsByBot.set(botName, new Set());
      for (const t of def.tool_names ?? []) outcomeToolsByBot.get(botName).add(t);
    }
  }
  const targetBots = new Set(outcomeToolsByBot.keys());

  // Pre-filter: target bot + likely-handover shape. "Likely handover"
  // includes ESCALATED and lastAction=Agent handover — either is
  // sufficient. (Some sessions log ESCALATED but have a non-handover
  // lastAction when the escalation came from a flow-level route.)
  const candidates = sessions.filter((s) => {
    const bot = resolveBotName(s);
    if (!targetBots.has(bot)) return false;
    const outcome = s.interactionOutcome ?? '';
    const last = s.lastAction ?? '';
    return outcome === 'ESCALATED' || last === 'Agent handover';
  });

  // Running stats keyed by bot. We only build the perBot entry when the
  // bot has at least one candidate — a bot with 0 handovers in the
  // window shouldn't appear in the final table.
  const perBot = new Map();
  const bump = (bot, isPremature, durationMs, sessionId) => {
    if (!perBot.has(bot)) {
      perBot.set(bot, {
        totalHandovers: 0,
        premature: 0,
        _sumDurationMs: 0,
        _durationCount: 0,
        prematureSessionIds: new Set(),
      });
    }
    const r = perBot.get(bot);
    r.totalHandovers += 1;
    if (isPremature) {
      r.premature += 1;
      if (sessionId) r.prematureSessionIds.add(sessionId);
    }
    if (Number.isFinite(durationMs)) {
      r._sumDurationMs += durationMs;
      r._durationCount += 1;
    }
  };

  let done = 0;
  for (const s of candidates) {
    if (opts.signal?.aborted) break;
    const botName = resolveBotName(s);
    const toolSet = outcomeToolsByBot.get(botName);
    const agentId = opts.agentIdByName?.[botName];
    const duration = Number(s.duration);

    if (!agentId || !toolSet) {
      // Can't fetch — treat as premature (we literally couldn't check).
      // Downside is biasing the number upward; better than silently
      // dropping sessions from the denominator. Log as premature.
      bump(botName, true, duration, s.sessionId);
      done += 1;
      opts.onProgress?.(done, candidates.length);
      continue;
    }

    let messages = [];
    try {
      const res = await opts.fetchMessages(agentId, s.sessionId);
      messages = res?.messages ?? [];
    } catch {
      // On a message-fetch error, we can't verify the bot tried its
      // workflow. Count as premature with the same reasoning as above
      // (keeps the denominator honest).
      bump(botName, true, duration, s.sessionId);
      done += 1;
      opts.onProgress?.(done, candidates.length);
      continue;
    }

    // Did ANY successful business-outcome tool fire?
    let anyOutcomeToolFired = false;
    outer: for (const m of messages) {
      const tools = m?.metadata?.tools ?? [];
      for (const t of tools) {
        const toolName = t.tool_name ?? t.name ?? '';
        if (t.state && t.state !== 'SUCCESS') continue;
        if (toolSet.has(toolName)) {
          anyOutcomeToolFired = true;
          break outer;
        }
      }
    }

    bump(botName, !anyOutcomeToolFired, duration, s.sessionId);
    done += 1;
    opts.onProgress?.(done, candidates.length);
  }

  const result = {};
  for (const [bot, r] of perBot.entries()) {
    result[bot] = {
      totalHandovers: r.totalHandovers,
      premature: r.premature,
      rate: r.totalHandovers ? Math.round((r.premature / r.totalHandovers) * 100) : 0,
      avgDurationMs: r._durationCount > 0 ? r._sumDurationMs / r._durationCount : null,
      prematureSessionIds: r.prematureSessionIds,
    };
  }

  return { perBot: result, totalScanned: candidates.length };
}

/**
 * Count the pre-filter candidates for a given sessions+catalog pair —
 * lets the UI preview "N sessions, ~Ms" before the user clicks Scan.
 */
export function countCandidates(sessions, catalog, agentIdToName = {}) {
  const outcomeTargetBots = new Set();
  for (const [botName, bot] of Object.entries(catalog?.bots ?? {})) {
    if (bot?.business_outcomes && Object.keys(bot.business_outcomes).length > 0) {
      outcomeTargetBots.add(botName);
    }
  }
  const resolve = (s) => s.agentName || (s.agentId && agentIdToName[s.agentId]) || '';
  let n = 0;
  for (const s of sessions) {
    if (!outcomeTargetBots.has(resolve(s))) continue;
    const outcome = s.interactionOutcome ?? '';
    const last = s.lastAction ?? '';
    if (outcome === 'ESCALATED' || last === 'Agent handover') n += 1;
  }
  return n;
}
